/**
 * Feedback Service
 *
 * This file provides the actual API service implementations for feedback-related operations.
 * These services make real HTTP requests to the backend API.
 */

import {
  DownloadFeedbackQuery,
  FeedbackFormData,
  FeedbackModel,
  FeedbackQuery,
  QueryDetail,
} from "@/types/feedback.model";
import { APIResponse } from "@/types";
import AXIOS from "@/axios";
import { removeEmptyProps } from "@/utils/constants";
import { buildAssignmentRecordApiParams } from "@/utils/assignmentRecordParams";
import moment from "moment";

function withQueryRecordParams(
  query: FeedbackQuery | DownloadFeedbackQuery
): Record<string, unknown> {
  const { recordType, gradeId, classId, ...rest } = query;

  return removeEmptyProps({
    ...rest,
    ...buildAssignmentRecordApiParams({ recordType, gradeId, classId }),
  });
}

/**
 * Get all feedbacks with optional filtering and pagination
 */
export const getAllFeedbackService = async (query: FeedbackQuery) => {
  if (query.fromDate && query.toDate) {
    query.fromDate = moment(new Date(query.fromDate).toISOString()).format(
      "DD-MM-YYYY"
    );
    query.toDate = moment(new Date(query.toDate).toISOString()).format(
      "DD-MM-YYYY"
    );
  }
  return AXIOS.get<APIResponse<FeedbackModel[]>>(`/asgmt/api/v1/fdbk/list`, {
    params: removeEmptyProps(query),
  });
};

/**
 * Get all feedbacks with optional filtering and pagination
 */
export const getAllQueryService = async (query: FeedbackQuery) => {
  if (query.fromDate && query.toDate) {
    query.fromDate = moment(new Date(query.fromDate).toISOString()).format(
      "DD-MM-YYYY"
    );
    query.toDate = moment(new Date(query.toDate).toISOString()).format(
      "DD-MM-YYYY"
    );
  }

  return AXIOS.get<APIResponse<QueryDetail[]>>(
    `/asgmt/api/v1/qry/detail/list`,
    {
      params: withQueryRecordParams(query),
    }
  );
};

export const addQueryService = async (feedback: FeedbackFormData) => {
  return AXIOS.post<APIResponse<{ message: string }>>(
    "/asgmt/api/v1/qry/add",
    feedback
  );
};

export const addQueryFeedbackService = async (feedback: FeedbackFormData) => {
  return AXIOS.post<APIResponse<{ message: string }>>(
    "/asgmt/api/v1/qry/fdbk/add",
    feedback
  );
};

export const addFeedbackService = async (feedback: FeedbackFormData) => {
  return AXIOS.post<APIResponse<{ message: string }>>(
    "/asgmt/api/v1/fdbk/add",
    feedback
  );
};

export const downloadFeedbackService = async (
  type: "xl" | "pdf" | "csv",
  query: DownloadFeedbackQuery
) => {
  if (query.fromDate && query.toDate) {
    query.fromDate = moment(new Date(query.fromDate).toISOString()).format(
      "DD-MM-YYYY"
    );
    query.toDate = moment(new Date(query.toDate).toISOString()).format(
      "DD-MM-YYYY"
    );
  }

  return AXIOS.get(`/asgmt/api/v1/fdbk/dwnld/${type}`, {
    params: removeEmptyProps(query),
    responseType: "blob",
  });
};
